import { useEffect, useRef } from "react"
import { zIndex } from "@onestore/hel/dist/settings"
import { KeyboardEventKeys } from "@onestore/hel/dist/typings/types"
import log from "~/lib/log"

interface ThuliumChatProps {
  id: string
}

declare global {
  interface Window {
    _tc?: object
  }
}

export default function ThuliumChat({ id }: ThuliumChatProps): null {
  const ref = useRef<HTMLScriptElement>()
  const zIndexThulium = zIndex.PageHeaderOrgMain + 10

  useEffect(() => {
    if (id) {
      log(`Setting up widget with ID: ${id}`, "ThuliumChat")

      const cmpVendor = process.env.GATSBY_THULIUM_CHAT_CMP_VENDOR

      const script = document.createElement("script")
      const style = document.createElement("style")
      style.setAttribute("type", "text/css")

      script.async = true

      if (cmpVendor) {
        script.type = "text/plain"
        script.setAttribute("class", "cmplazyload")
        script.setAttribute("data-cmp-vendor", cmpVendor)
        script.setAttribute("data-cmp-purpose", "c51")
        script.setAttribute(
          "data-cmp-src",
          `https://cdn.thulium.com/apps/chat-widget/chat-loader.js?hash=${id}`
        )
      } else {
        script.type = "text/javascript"
        script.src = `https://cdn.thulium.com/apps/chat-widget/chat-loader.js?hash=${id}`
      }

      style.innerHTML =
        `
      .thulium-chat-wrapper { z-index: ` +
        zIndexThulium +
        ` !important; width: 85px; height: 85px; bottom: 16px !important; right: 16px !important; }
      `

      document.head.append(script)
      document.head.append(style)

      ref.current = script

      const intervalId = setInterval(() => {
        const wrapperElement = document.querySelector(".thulium-chat-wrapper")

        if (wrapperElement) {
          wrapperElement.setAttribute("role", "region")
          wrapperElement.setAttribute("aria-label", "Chat")
          wrapperElement.setAttribute("tabindex", "0")

          wrapperElement.addEventListener("keydown", (event: KeyboardEvent) => {
            if (
              event.key === KeyboardEventKeys.SPACE ||
              event.key === KeyboardEventKeys.ENTER
            ) {
              event.preventDefault()

              if (typeof window._tc === "function") {
                window._tc("open_chat")
              }
            }
          })

          clearInterval(intervalId)
        }
      }, 100)
    }

    return () => {
      log(`Cleaning up widget ID: ${id}`, "ThuliumChat")

      document.querySelectorAll(".thulium-chat-wrapper").forEach((element) => {
        element.remove()
      })

      if (window._tc) {
        delete window._tc
      }

      const currentRef = ref.current

      if (!currentRef) {
        return
      }

      currentRef.remove()
    }
  }, [id])

  return null
}
